/**
 * WHAT: Authentication Service
 * WHY: Handle all authentication-related operations and token management
 * HOW: Using fetch API and JWT tokens stored in httpOnly cookies
 */

import CONFIG from '../config/config.js';
import csrfService from './csrf.service.js';

class AuthService {
    constructor() {
        this.API_URL = CONFIG.api.baseUrl;
        this.user = null;
        this.isAuthenticated = false;
        this.retryCount = 0;
        this.maxRetries = 3;
        this.refreshSubscribers = [];
        this.isRefreshing = false;

        // Try to restore auth state
        this.restoreAuthState();
    }

    /**
     * WHAT: Restore authentication state
     * WHY: Handle private browsing and page refreshes
     * HOW: Verify auth using httpOnly cookies (secure, not accessible to JavaScript)
     * SECURITY: Tokens are only in httpOnly cookies, never in localStorage/sessionStorage
     */
    async restoreAuthState() {
        try {
            // WHAT: Verify authentication using httpOnly cookie
            // WHY: Tokens are stored securely in httpOnly cookies (not accessible to JavaScript)
            // HOW: Call verify endpoint - cookies are sent automatically with credentials: 'include'
            // SECURITY: No token storage in localStorage/sessionStorage (XSS protection)
            const response = await fetch(`${this.API_URL}/api/auth/verify`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include' // Automatically sends httpOnly cookies
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    this.user = data.data?.user || data.user || data;
                    this.isAuthenticated = true;
                    
                    // WHAT: Store user role in sessionStorage (non-sensitive data)
                    // WHY: Role is needed for UI but not sensitive like tokens
                    // HOW: Store only role, never tokens
                    const userRole = this.user.role;
                    if (userRole) {
                        try {
                            sessionStorage.setItem('userRole', userRole);
                        } catch (e) {
                            // sessionStorage unavailable - non-critical
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error restoring auth state:', error);
        }
    }

    /**
     * WHAT: Store authentication token
     * WHY: Tokens are stored in httpOnly cookies by backend (secure)
     * HOW: Backend sets httpOnly cookie automatically on login
     * SECURITY: This method is deprecated - tokens should NEVER be stored in localStorage/sessionStorage
     * IMPORTANCE: Prevents XSS attacks from stealing tokens
     * 
     * @deprecated Tokens are now stored in httpOnly cookies only. This method does nothing.
     */
    storeAuthToken(token) {
        // SECURITY: Tokens are stored in httpOnly cookies by the backend
        // DO NOT store tokens in localStorage/sessionStorage (XSS vulnerability)
        // The backend automatically sets httpOnly cookies on login
    }
    
    /**
     * WHAT: Get stored authentication token
     * WHY: Retrieve token for API requests
     * HOW: Tokens are in httpOnly cookies (not accessible to JavaScript)
     * SECURITY: Cannot read httpOnly cookies from JavaScript - this method returns null
     * IMPORTANCE: API requests should use credentials: 'include' to send cookies automatically
     * 
     * @deprecated Tokens are in httpOnly cookies and cannot be read by JavaScript.
     * Use credentials: 'include' in fetch requests instead.
     * @returns {null} Always returns null - tokens are in httpOnly cookies
     */
    getStoredAuthToken() {
        // SECURITY: Tokens are stored in httpOnly cookies by the backend
        // httpOnly cookies are NOT accessible to JavaScript (XSS protection)
        // API requests should use credentials: 'include' to send cookies automatically
        // DO NOT try to read tokens from localStorage/sessionStorage (security risk)
        return null;
    }

    /**
     * WHAT: Verify user authentication
     * WHY: Check if user is logged in
     * HOW: Check localStorage for token and validate with backend
     */
    async verifyAuth() {
        try {
            // First check if this is an initial login
            const isInitialLogin = sessionStorage.getItem('initialLogin') === 'true';
            const lastAuthCheck = parseInt(sessionStorage.getItem('lastAuthCheck') || '0');
            const timeSinceAuth = Date.now() - lastAuthCheck;

            // If this is an initial login within 2 seconds, allow access
            if (isInitialLogin && timeSinceAuth < 2000) {
                return true;
            }

            // WHAT: Verify auth with backend using httpOnly cookies
            // WHY: Tokens are in httpOnly cookies (not accessible to JavaScript)
            // HOW: Use credentials: 'include' to send cookies automatically
            // SECURITY: No Authorization header needed - backend reads from cookie
            const response = await fetch(`${this.API_URL}/api/auth/verify`, {
                method: 'GET',
                credentials: 'include', // Automatically sends httpOnly cookies
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                    // No Authorization header - backend reads token from httpOnly cookie
                }
            });

            if (!response.ok) {
                return false;
            }

            const data = await response.json();
            if (!data.success) {
                return false;
            }

            // Update user data
            this.user = data.data?.user || data.user || data;
            this.isAuthenticated = true;

            return true;
        } catch (error) {
            console.error('Auth verification failed:', error);
            return false;
        }
    }

    /**
     * WHAT: Get user role
     * WHY: Check user permissions
     * HOW: Get from cached user data or fetch from backend
     */
    async getUserRole() {
        try {
            // First check if we have the role in session storage
            const storedRole = sessionStorage.getItem('userRole');
            if (storedRole) {
                return storedRole;
            }

            // If not in session storage, check the user object
            if (this.user?.role) {
                return this.user.role;
            }

            // If still not found, verify auth to get fresh user data
            const isAuthed = await this.verifyAuth();
            if (!isAuthed) {
                return null;
            }

            return this.user?.role || null;
        } catch (error) {
            console.error('Error getting user role:', error);
            return null;
        }
    }

    /**
     * WHAT: Login user
     * WHY: Authenticate user
     * HOW: Send credentials to backend and store token
     */
    async login(credentials) {
        try {
            // Add CSRF token to credentials
            const credentialsWithCSRF = await csrfService.addTokenToJson(credentials);
            
            const response = await fetch(`${this.API_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(credentialsWithCSRF),
                credentials: 'include'  // Important for cookies
            });

            const responseText = await response.text();

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                console.error('Failed to parse login response:', e);
                throw new Error('Invalid server response');
            }

            if (!response.ok) {
                throw new Error(data.message || 'Login failed');
            }

            // Store user data
            this.user = data.data?.user || data.user || data;
            this.isAuthenticated = true;
            
            // SECURITY: Tokens are stored in httpOnly cookies by the backend automatically
            // DO NOT store tokens in localStorage/sessionStorage (XSS vulnerability)
            // The backend sets httpOnly cookie on login - no JavaScript access needed

            // Set session storage flags
            try {
                sessionStorage.setItem('lastAuthCheck', Date.now().toString());
                sessionStorage.setItem('initialLogin', 'true');
                const userRole = this.user.role;
                if (userRole) {
                    sessionStorage.setItem('userRole', userRole);
                }
            } catch (e) {
                // sessionStorage unavailable - non-critical
            }

            // Determine redirect path based on user role
            const userRole = this.user.role;
            
            let redirectPath;
            if (userRole === 'admin') {
                try {
                    sessionStorage.setItem('preventDashboardRedirect', 'false');
                } catch (e) {
                    // sessionStorage unavailable - non-critical
                }
                redirectPath = '/admin-dashboard.html';
            } else if (userRole === 'user') {
                try {
                    sessionStorage.setItem('preventDashboardRedirect', 'true');
                } catch (e) {
                    // sessionStorage unavailable - non-critical
                }
                redirectPath = '/admin.html';
            } else {
                try {
                    sessionStorage.setItem('preventDashboardRedirect', 'true');
                } catch (e) {
                    // sessionStorage unavailable - non-critical
                }
                redirectPath = '/admin.html';
            }

            // Wait a moment for storage operations to complete
            await new Promise(resolve => setTimeout(resolve, 100));

            window.location.replace(redirectPath);
            
            return this.user;
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    }

    /**
     * WHAT: Logout user
     * WHY: End user session
     * HOW: Clear local storage and redirect
     */
    async logout() {
        try {
            // WHAT: Get CSRF token for security
            // WHY: Prevent CSRF attacks on state-changing operations (logout)
            // HOW: Request CSRF token from backend
            const csrfToken = await csrfService.getToken();

            // WHAT: Logout request
            // WHY: Clear httpOnly cookie on server
            // HOW: POST to logout endpoint - backend clears cookie
            await fetch(`${this.API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include', // Send httpOnly cookie to be cleared
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken // CSRF token for security
                }
            });
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            // SECURITY: No need to remove from localStorage - tokens are in httpOnly cookies only
            // Clear user data and redirect
            this.user = null;
            this.isAuthenticated = false;
            // Clear non-sensitive session data
            try {
                sessionStorage.removeItem('userRole');
                sessionStorage.removeItem('lastAuthCheck');
                sessionStorage.removeItem('initialLogin');
            } catch (e) {
                // Ignore errors
            }
            window.location.href = '/login.html';
        }
    }

    /**
     * WHAT: Get authentication status
     * WHY: Check if user is currently authenticated
     * HOW: Return cached status or verify with backend
     */
    async getAuthStatus() {
        // If we have a cached status and user data, return it
        if (this.isAuthenticated && this.user) {
            return true;
        }

        // Otherwise verify with backend
        return await this.verifyAuth();
    }

    /**
     * WHAT: Get current user data
     * WHY: Access user information
     * HOW: Return cached user data or fetch from backend
     */
    async getUserData() {
        if (this.user) {
            return this.user;
        }

        // Try to verify auth to get fresh user data
        const isAuthed = await this.verifyAuth();
        if (!isAuthed) {
            return null;
        }

        return this.user;
    }

    /**
     * WHAT: Make authenticated API requests
     * WHY: Handle token refresh and retries automatically
     * HOW: Using fetch with interceptors
     */
    async fetchWithAuth(url, options = {}) {
        try {
            // SECURITY: Tokens are in httpOnly cookies (not accessible to JavaScript)
            // Use credentials: 'include' to send cookies automatically
            // Backend reads token from cookie - no Authorization header needed
            let response = await fetch(url, {
                ...options,
                credentials: 'include',  // Automatically sends httpOnly cookies
                headers: {
                    'Accept': 'application/json',
                    // Don't set Content-Type for FormData - let browser set it automatically
                    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                    // No Authorization header - backend reads token from httpOnly cookie
                    ...options.headers
                }
            });

            // Check if authentication failed
            if (response.status === 401) {
                // Try to verify auth state (this also uses credentials: 'include')
                const isAuthed = await this.verifyAuth();
                if (!isAuthed) {
                    throw new Error('Authentication required');
                }

                // Retry the request - cookies are sent automatically with credentials: 'include'
                response = await fetch(url, {
                    ...options,
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json',
                        // Don't set Content-Type for FormData - let browser set it automatically
                        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                        // No Authorization header - backend reads token from httpOnly cookie
                        ...options.headers
                    }
                });
            }

            // Stale CSRF token is common after login or long admin sessions (multipart uploads)
            if (response.status === 403) {
                const errorBody = await response.clone().json().catch(() => ({}));
                const csrfMessage = String(errorBody.message || '').toLowerCase();
                if (csrfMessage.includes('csrf') && !options._csrfRetried) {
                    csrfService.clearToken();
                    const freshCsrf = await csrfService.getToken();
                    response = await fetch(url, {
                        ...options,
                        _csrfRetried: true,
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                            ...options.headers,
                            'X-CSRF-Token': freshCsrf
                        }
                    });
                }
            }

            return response;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    /**
     * WHAT: Refresh the access token
     * WHY: Maintain user session without requiring re-login
     * HOW: Using refresh token endpoint
     */
    async refreshToken() {
        try {
            if (this.isRefreshing) {
                return new Promise(resolve => {
                    this.subscribeTokenRefresh(token => {
                        resolve(token);
                    });
                });
            }

            this.isRefreshing = true;

            const response = await fetch(`${this.API_URL}/api/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Token refresh failed');
            }

            const data = await response.json();
            this.isRefreshing = false;
            this.onRefreshComplete(data.token);
            return data.token;

        } catch (error) {
            this.isRefreshing = false;
            console.error('Error refreshing token:', error);
            this.isAuthenticated = false;
            window.location.href = '/login.html?error=session_expired';
            throw error;
        }
    }

    /**
     * WHAT: Subscribe to token refresh
     * WHY: Queue requests that failed due to token expiration
     * HOW: Using callback functions
     */
    subscribeTokenRefresh(callback) {
        this.refreshSubscribers.push(callback);
    }

    /**
     * WHAT: Notify subscribers after token refresh
     * WHY: Retry queued requests with new token
     * HOW: Executing callback functions
     */
    onRefreshComplete(token) {
        this.refreshSubscribers.forEach(callback => callback(token));
        this.refreshSubscribers = [];
    }

    /**
     * WHAT: Check if a navigation should be allowed
     * WHY: Prevent unwanted redirects during initial login
     * HOW: Using session storage flags and timestamps
     */
    shouldAllowNavigation(targetPage) {
        const lastAuthCheck = parseInt(sessionStorage.getItem('lastAuthCheck') || '0');
        const isInitialLogin = sessionStorage.getItem('initialLogin') === 'true';
        const preventDashboardRedirect = sessionStorage.getItem('preventDashboardRedirect') === 'true';
        const timeSinceAuth = Date.now() - lastAuthCheck;

        // If this is the initial login and we're within 2 seconds of auth
        if (isInitialLogin && timeSinceAuth < 2000) {
            // Only prevent navigation to admin dashboard for non-admin users
            if (targetPage === '/admin-dashboard.html') {
                if (preventDashboardRedirect) {
                    return false;
                }
                // Allow admin users to access the dashboard
                return true;
            }
        }

        // For all other cases, allow navigation
        return true;
    }

    /**
     * WHAT: Get authentication headers
     * WHY: Provide headers for authenticated requests
     * HOW: Use credentials: 'include' to send httpOnly cookies automatically
     * SECURITY: Tokens are in httpOnly cookies (not accessible to JavaScript)
     * IMPORTANCE: API requests should use credentials: 'include' instead of Authorization headers
     * 
     * @returns {Object} Headers object (without Authorization - cookies sent automatically)
     */
    async getAuthHeaders() {
        // SECURITY: Tokens are stored in httpOnly cookies by the backend
        // httpOnly cookies are NOT accessible to JavaScript (XSS protection)
        // API requests should use credentials: 'include' to send cookies automatically
        // The backend middleware will read the token from the cookie
        
        // WHAT: Return basic headers without Authorization
        // WHY: Authorization header not needed - cookies are sent automatically with credentials: 'include'
        // HOW: Return Accept header only
        return {
            'Accept': 'application/json'
        };
    }
}

// Export singleton instance
export default new AuthService(); 